/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import {
  Popover,
  PopoverTrigger,
  PopoverContent,
  PopoverHeader,
  PopoverBody,
  PopoverFooter,
  Input as ChakraInput,
  VStack,
  HStack,
  Divider,
  Text,
  useDisclosure,
} from '@chakra-ui/react';
import FilterIcon from '../../../../assets/svg/filter-icon.svg?react';
import CustomSelect from '../../CustomSelect';
import Button from '../../Button';
import { useGetCategoriesForFilter, useGetSubCategoriesForFilter } from '@/services/manageProduct';
import { RoleTitle } from '@/services/userManagement/types';

interface FilterPopoverProps {
  filters?: {
    start_date?: string;
    end_date?: string;
    from_date?: string;
    to_date?: string;
    category?: any;
    subcategory?: any;
    assign_title?: string;
    user_status?: boolean | null;
  };
  setFilters?: React.Dispatch<
    React.SetStateAction<{
      start_date?: string;
      end_date?: string;
      from_date?: string;
      to_date?: string;
      category?: any;
      subcategory?: any;
      assign_title?: string;
      user_status?: boolean | null;
    }>
  >;
  applyFilters?: () => void;
  resetFilters?: () => void;
  setPage?: () => void;
  hideCategoryFilter?: boolean;
  rolesFilter?: RoleTitle[];
  dateFilterType?: 'start_end' | 'from_to';
  restrictDateRange?: boolean;
  sendCategoryIds?: boolean;
  hideSubcategoryFilter?: boolean;
}

const FilterPopover: React.FC<FilterPopoverProps> = ({
  filters = {},
  setFilters,
  applyFilters,
  resetFilters,
  setPage,
  hideCategoryFilter,
  rolesFilter,
  dateFilterType = 'start_end',
  hideSubcategoryFilter = false,
  restrictDateRange = false,
  sendCategoryIds = false, // Default: send names (safe for other pages)
}) => {
  const { isOpen, onOpen, onClose } = useDisclosure();
  const initialFocusRef = useRef<HTMLInputElement>(null);

  const [localFilters, setLocalFilters] = useState(filters);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [selectedSubcategory, setSelectedSubcategory] = useState<string | null>(null);
  const [selectedRole, setSelectedRole] = useState<string>('');
  const [selectedStatus, setSelectedStatus] = useState<string | null>(null);

  useEffect(() => {
    if (isOpen) {
      setLocalFilters(filters);
      setSelectedCategory(filters.category?.toString() || null);
      setSelectedSubcategory(filters.subcategory?.toString() || null);
      setSelectedRole(filters.assign_title || '');
      setSelectedStatus(
        filters.user_status === true ? 'true' : filters.user_status === false ? 'false' : null,
      );
    }
  }, [filters, isOpen]);

  const { data: categoryData } = useGetCategoriesForFilter({ enabled: !hideCategoryFilter });
  const selectedCategoryId = selectedCategory ? parseInt(selectedCategory) : undefined;

  const { data: subCategoryData } = useGetSubCategoriesForFilter(selectedCategoryId);
  const isSubCategoryDisabled = !selectedCategory;

  const startDateField = dateFilterType === 'from_to' ? 'from_date' : 'start_date';
  const endDateField = dateFilterType === 'from_to' ? 'to_date' : 'end_date';
  const startDateLabel = dateFilterType === 'from_to' ? 'From Date' : 'Start Date';
  const endDateLabel = dateFilterType === 'from_to' ? 'To Date' : 'End Date';

  const today = new Date().toISOString().split('T')[0];

  const getMaxEndDate = () => {
    if (!restrictDateRange) return today;
    const startValue = localFilters[startDateField];
    if (!startValue) return today;
    const start = new Date(startValue);
    const maxEnd = new Date(start);
    maxEnd.setMonth(start.getMonth() + 3);
    maxEnd.setDate(maxEnd.getDate() - 1);
    const maxDate = new Date(today);
    return maxEnd > maxDate ? today : maxEnd.toISOString().split('T')[0];
  };

  const maxEndDate = getMaxEndDate();

  useEffect(() => {
    if (!restrictDateRange) return;
    const currentEnd = localFilters[endDateField];
    if (currentEnd && maxEndDate && currentEnd > maxEndDate) {
      setLocalFilters((prev) => ({ ...prev, [endDateField]: '' }));
    }
  }, [localFilters[startDateField], maxEndDate, endDateField, restrictDateRange]);

  const handleApply = () => {
    if (!setFilters) return;

    const cleanedFilters: Partial<typeof filters> = {};

    if (localFilters[startDateField]) cleanedFilters[startDateField] = localFilters[startDateField];
    if (localFilters[endDateField]) cleanedFilters[endDateField] = localFilters[endDateField];

    // ──────────────────────────────────────────────────────────────
    // Send ID or Name depending on sendCategoryIds prop
    // ──────────────────────────────────────────────────────────────
    if (selectedCategory) {
      cleanedFilters.category = sendCategoryIds
        ? selectedCategory // ID
        : categoryOptions.find((c) => c.id === selectedCategory)?.label || ''; // Name
    }
    if (selectedSubcategory) {
      cleanedFilters.subcategory = sendCategoryIds
        ? selectedSubcategory
        : subcategoryOptions.find((c) => c.id === selectedSubcategory)?.label || '';
    }

    // Other filters unchanged (names/booleans)
    if (localFilters.assign_title) cleanedFilters.assign_title = localFilters.assign_title;
    if (localFilters.user_status !== null && localFilters.user_status !== undefined) {
      cleanedFilters.user_status = localFilters.user_status;
    }

    setFilters(cleanedFilters);
    applyFilters?.();
    onClose();
  };

  const handleResetFilters = () => {
    setLocalFilters({
      start_date: '',
      end_date: '',
      from_date: '',
      to_date: '',
      category: '',
      subcategory: '',
      assign_title: '',
      user_status: null,
    });
    setSelectedCategory(null);
    setSelectedSubcategory(null);
    setSelectedRole('');
    setSelectedStatus(null);
    resetFilters?.();
    setPage?.();
  };

  const categoryOptions =
    categoryData?.map((c) => ({
      id: c.id.toString(),
      label: c.name,
    })) || [];

  const subcategoryOptions =
    subCategoryData?.map((c) => ({
      id: c.id.toString(),
      label: c.name,
    })) || [];

  const roleFilterOptions =
    rolesFilter?.map((role) => ({
      id: role.name,
      label: role.name,
    })) || [];

  const statusOptions = [
    { id: 'true', label: 'Active' },
    { id: 'false', label: 'Inactive' },
  ];

  return (
    <Popover
      isOpen={isOpen}
      onOpen={onOpen}
      onClose={onClose}
      initialFocusRef={initialFocusRef}
      closeOnBlur={true}
    >
      <PopoverTrigger>
        <Button
          style={{
            fontSize: '14px',
            padding: '10px 12px',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            backgroundColor: 'transparent',
            color: '#090F1A',
            borderRadius: '12px',
            border: '1px solid #D0D5DD',
            boxShadow: '0px 1px 2px 0px #1018280D',
          }}
          _focus={{
            borderColor: 'brand.primary.DEFAULT',
            color: 'brand.primary.DEFAULT',
            boxShadow: '0 0 0 1px brand.primary.DEFAULT',
          }}
        >
          <FilterIcon style={{ marginRight: '8px', paddingBottom: '4px' }} />
          Filter By
        </Button>
      </PopoverTrigger>

      <PopoverContent minWidth={{ base: '300px', md: '420px' }} marginLeft="10px">
        <PopoverHeader
          px={4}
          py={3}
          bg={'background.secondary'}
          fontSize={'sm'}
          fontWeight={'medium'}
          fontFamily={'secondary'}
          color={'#101828'}
        >
          Filter Options
        </PopoverHeader>

        <PopoverBody p={0}>
          <VStack spacing={1}>
            <HStack
              px={4}
              py={4}
              width={'100%'}
              spacing={2}
              align="center"
              display={rolesFilter && rolesFilter.length > 0 ? 'none' : 'flex'}
            >
              <ChakraInput
                ref={initialFocusRef}
                type="date"
                value={localFilters[startDateField] || ''}
                onChange={(e) =>
                  setLocalFilters((prev) => ({ ...prev, [startDateField]: e.target.value }))
                }
                max={today}
                placeholder={startDateLabel}
              />
              <span style={{ margin: '0 8px', fontWeight: 'bold' }}> - </span>
              <ChakraInput
                type="date"
                value={localFilters[endDateField] || ''}
                onChange={(e) =>
                  setLocalFilters((prev) => ({ ...prev, [endDateField]: e.target.value }))
                }
                min={localFilters[startDateField] || undefined}
                max={restrictDateRange ? maxEndDate || today : today}
                disabled={!localFilters[startDateField] && restrictDateRange}
                placeholder={endDateLabel}
              />
            </HStack>
            <Divider />

            {hideCategoryFilter !== true && (
              <>
                <HStack px={4} py={4} width={'100%'}>
                  <CustomSelect
                    label="Select Category"
                    options={categoryOptions}
                    placeholder="Select Category"
                    selectedValue={selectedCategory}
                    onChange={(id) => {
                      setSelectedCategory(id as string | null);
                      setSelectedSubcategory(null);
                      setLocalFilters((prev) => ({
                        ...prev,
                        category: id || '',
                        subcategory: '',
                      }));
                    }}
                    isFullWidth={true}
                  />
                </HStack>
                {!hideSubcategoryFilter && (
                  <>
                    <Divider />
                    <HStack px={4} py={4} width={'100%'}>
                      <CustomSelect
                        label="Select Sub-Category"
                        options={subcategoryOptions}
                        placeholder={
                          isSubCategoryDisabled ? 'Select category first' : 'Select Subcategory'
                        }
                        selectedValue={selectedSubcategory}
                        onChange={(id) => {
                          setSelectedSubcategory(id as string | null);
                          setLocalFilters((prev) => ({
                            ...prev,
                            subcategory: id || '',
                          }));
                        }}
                        isFullWidth={true}
                        isDisabled={isSubCategoryDisabled}
                        noOptionsMessage={
                          isSubCategoryDisabled ? '' : 'No sub-categories available'
                        }
                      />
                    </HStack>
                  </>
                )}
              </>
            )}

            {rolesFilter && rolesFilter.length > 0 && (
              <>
                <HStack px={4} py={4} width={'100%'}>
                  <CustomSelect
                    label="Role"
                    options={roleFilterOptions}
                    placeholder="Select Role"
                    selectedValue={selectedRole}
                    onChange={(val) => {
                      setSelectedRole(val);
                      setLocalFilters((prev) => ({
                        ...prev,
                        assign_title: val || '',
                      }));
                    }}
                    isFullWidth={true}
                  />
                </HStack>
                <HStack px={4} py={4} width={'100%'}>
                  <CustomSelect
                    label="Status"
                    options={statusOptions}
                    placeholder="Select Status"
                    selectedValue={selectedStatus}
                    onChange={(val) => {
                      setSelectedStatus(val);
                      setLocalFilters((prev) => ({
                        ...prev,
                        user_status: val === 'true' ? true : val === 'false' ? false : null,
                      }));
                    }}
                    isFullWidth={true}
                  />
                </HStack>
              </>
            )}
          </VStack>
        </PopoverBody>

        <Divider />

        <PopoverFooter
          display="flex"
          justifyContent="flex-end"
          px={4}
          py={3}
          bg={'background.secondary'}
        >
          <HStack justifyContent={'space-between'} width={'100%'}>
            <Text as={'button'} onClick={handleResetFilters} color={'brand.primary.DEFAULT'}>
              Reset
            </Text>
            <Button onClick={handleApply}>Apply</Button>
          </HStack>
        </PopoverFooter>
      </PopoverContent>
    </Popover>
  );
};

export default FilterPopover;
